import { useState } from "react";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Nav } from "@/components/marketing/Nav";
import { CtaFooter } from "@/components/marketing/CtaFooter";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2";

const SERVICES = [
  "Community Echocardiography Sessions",
  "Rhythm Monitoring",
  "ABPM",
  "Full Capacity Proposal",
];

const initial = { name: "", organization: "", role: "", email: "", phone: "", service: SERVICES[0], message: "" };

const Section = ({ title, children, testId }) => (
  <Reveal>
    <div className="border-t border-navy/10 pt-10 mt-16" data-testid={testId}>
      <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-6">{title}</h2>
      <div className="space-y-5 text-sm lg:text-base text-navy/60 leading-relaxed max-w-3xl">
        {children}
      </div>
    </div>
  </Reveal>
);

const changes = [
  ["Shorter Wait Times", "Community delivery brings the investigation closer to the patient and forward in the pathway."],
  ["Fewer Secondary Care Referrals", "Where the echo answers the question, the hospital visit never needs to happen."],
  ["Better Attendance", "Among patients who struggle to reach a hospital site, a local session simply gets done."],
  ["Faster Decisions", "Management stays with the clinician who knows the patient."],
];

export default function NhsPrimaryCare() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        organization: form.organization,
        role: form.role,
        email: form.email,
        phone: form.phone,
        enquiry_type: `NHS Primary Care — ${form.service}`,
        message: form.message,
      });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen" data-testid="nhs-primary-care-page">
      <Nav />

      <section className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
            NHS Primary Care & PCNs
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4169E1] leading-tight max-w-4xl" data-testid="nhs-title">
            Community Echocardiography for Primary Care
          </h1>
          <p className="mt-8 text-base text-navy/60 leading-relaxed max-w-2xl">
            Echocardiography is one of the standard diagnostic tests where demand has
            grown faster than capacity, and the consequence is felt in primary care.
          </p>
          <p className="mt-5 text-base text-navy font-medium leading-relaxed max-w-2xl border-l-2 border-gold pl-5">
            We provide community adult transthoracic echocardiography delivered within
            primary care, to the same standard as a hospital department, in your own
            practice or another agreed community site.
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <Reveal>
          <div className="border-t border-navy/10 pt-10 mt-4" data-testid="nhs-changes">
            <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-8">
              What this changes for your population
            </h2>
            <div className="grid sm:grid-cols-2 gap-px bg-navy/10 border border-navy/10 max-w-4xl">
              {changes.map(([head, body], i) => (
                <div key={head} className="bg-white p-8" data-testid={`nhs-change-${i}`}>
                  <p className="font-serif text-lg font-semibold text-navy mb-2">
                    <span className="text-gold mr-2">✦</span>
                    {head}
                  </p>
                  <p className="text-xs text-navy/60 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Section title="What we provide" testId="nhs-provide">
          <p>
            Cardiac ultrasound equipment and an accredited workforce for agreed sessions.
            Acquisition and reporting to the British Society of Echocardiography minimum
            dataset, by a practitioner accredited by the BSE and the European Association
            of Cardio-Vascular Imaging who assesses and examines for both bodies.
          </p>
          <p>
            Image storage and transfer arranged with the commissioning organisation.
            Participation in your audit, quality assurance and incident reporting
            processes, with activity and turnaround reporting against agreed KPIs.
          </p>
        </Section>

        <Section title="What stays with you" testId="nhs-stays">
          <p>
            Referral triage and vetting, clinical responsibility for onward management,
            the patient record, and the registration under which the activity is
            delivered — unless otherwise agreed in writing.
          </p>
        </Section>

        <Section title="Quality and governance" testId="nhs-quality">
          <p>
            Community echocardiography only reduces secondary care demand if the report
            can be relied upon. A study that gets repeated at the hospital has cost the
            system twice.
          </p>
          <p>
            Every study is acquired and reported by the same accredited practitioner, so
            there is no separation of scanning from reporting and no inter-operator
            variability across your cohort. Studies are acquired to a defined minimum
            dataset, with image quality and completeness auditable.
          </p>
          <p>
            Reports are written in a structured format agreed with you, with clear
            conclusions rather than lists of measurements. Escalation criteria define
            which findings trigger same-day contact, which trigger routine referral, and
            which need no action. We will submit a sample of studies for external peer
            review at your request.
          </p>
        </Section>
      </div>

      <section id="enquire" className="bg-navy py-24 mt-16 scroll-mt-24" data-testid="nhs-enquiry-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
                Discuss Capacity
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ivory leading-tight mb-6">
                Tell us about your community.
              </h2>
              <p className="text-sm text-ivory/60 leading-relaxed max-w-md">
                Share your practice or PCN details, approximate patient volumes and
                preferred session pattern, and we will respond within one working day.
              </p>
              <p className="mt-6 text-xs text-ivory/40 leading-relaxed max-w-md border-l-2 border-gold/50 pl-4">
                Please do not include any patient-identifiable information in this form.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-gold/50 bg-ivory p-10" data-testid="nhs-form-success">
                <h3 className="font-serif text-2xl font-semibold text-navy mb-3">Enquiry received.</h3>
                <p className="text-sm text-navy/60 leading-relaxed">
                  Thank you — the CVI clinical team will review your requirements and
                  respond within one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="nhs-enquiry-form">
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="nhs-name-input" className={inputCls} placeholder="Dr Jane Smith" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Practice / PCN</label>
                  <input required value={form.organization} onChange={set("organization")} data-testid="nhs-organization-input" className={inputCls} placeholder="Riverside PCN" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Role</label>
                  <input value={form.role} onChange={set("role")} data-testid="nhs-role-input" className={inputCls} placeholder="PCN Clinical Director / Practice Manager" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Email</label>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="nhs-email-input" className={inputCls} placeholder="you@nhs.net" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Telephone</label>
                  <input type="tel" value={form.phone} onChange={set("phone")} data-testid="nhs-phone-input" className={inputCls} placeholder="07…" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Service of Interest</label>
                  <select value={form.service} onChange={set("service")} data-testid="nhs-service-select" className={inputCls}>
                    {SERVICES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={`${labelCls} !text-ivory/50`}>Message</label>
                  <textarea rows={5} required value={form.message} onChange={set("message")} data-testid="nhs-message-input" className={inputCls} placeholder="Approximate patient volumes, current waiting times, preferred session pattern…" />
                </div>

                {error && (
                  <p data-testid="nhs-form-error" className="sm:col-span-2 text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="nhs-submit-button"
                  className="sm:col-span-2 w-full bg-gold text-navy font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-champagne transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Discuss Capacity"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CtaFooter />
    </div>
  );
}
